/*
 * BuildDocs пользовательский интерфейс version 0.1.0
 *
 * Copyright © 2022 ООО АДАПТ info@acfs.spb.ru
 * You may use, distribute and modify this code under the
 * terms of the ООО АДАПТ license
 */

import { FC, useContext, useMemo } from 'react'

import {
  ApprovalState,
  billingEngineQueries,
  InspectionState,
  useCheckAndCommitOperation,
  useCheckOperation,
  useCoordinator,
  UUID,
} from '@builddocs/data-management'
import { AOSRType, SecondaryActType } from '@builddocs/filestore-api'
import { BillingTooltipModal, DeviationDialog } from '@builddocs/mui'
import {
  PENDING,
  statusColor,
  statusIcon,
  theStateWithDescription,
  UserInfoPopoverWithStatus,
} from '@builddocs/mui'
import { PaywallContext } from '@builddocs/system'
import { LoadingButton } from '@mui/lab'
import { Box, Button, Paper, Stack, Typography } from '@mui/material'
import { useQuery } from 'react-query'

interface IInspectActionProps {
  act: AOSRType | SecondaryActType
  handleToInspection: (actId: string) => Promise<void | { skipped: boolean }>
  handleDeviate: (
    body: Record<string, any>,
    additionalInfo: Record<string, any>,
    onSuccess: () => any,
  ) => void
  userEmploymentId: UUID
  canInspect: boolean
  canSendToSigning: boolean
  isDisabled?: boolean
}

export const InspectorAction: FC<IInspectActionProps> = ({
  act,
  handleToInspection,
  handleDeviate,
  userEmploymentId,
  canSendToSigning,
  isDisabled = false,
}: IInspectActionProps) => {
  const { projectId = '' } = useCoordinator()
  const { data: availableLimit } = useQuery(
    billingEngineQueries.getAvailableLimit({
      projectId,
      limitationOperationCode: 'SEND_DOC_TO_CHECK',
    }),
  )
  const { checkAndCommitOperation, isLoading } = useCheckAndCommitOperation()
  const { checkOperation } = useCheckOperation()
  const { open: openPaywall } = useContext(PaywallContext)
  const hasLegacyDuplicateAssignees = Array.isArray(act.actBannerTypes)
    ? act.actBannerTypes.includes('LEGACY_DUPLICATE_ASSIGNEES')
    : act.actBannerTypes === 'LEGACY_DUPLICATE_ASSIGNEES'

  const inspectionState = useMemo(() => {
    const states = act.inspectors?.map(user => user.inspectionState)

    if (!(states?.length > 0)) {
      return ''
    }
    if (act.approvalStatus !== ApprovalState.APPROVAL) {
      if (states.includes(InspectionState.REJECTED)) {
        return 'Отклонён'
      } else if (
        !states.includes(InspectionState.PENDING) ||
        !states.includes(InspectionState.UNDEFINED)
      ) {
        return 'Проверен'
      } else {
        return 'На проверке'
      }
    } else {
      return 'На проверке'
    }
  }, [act])

  if (!act.closingId) {
    return (
      <Typography color="text.secondary">
        Для начала проверки добавьте этот акт в закрытие
      </Typography>
    )
  }

  if (
    canSendToSigning &&
    (act.approvalStatus === ApprovalState.NEW ||
      act.approvalStatus === ApprovalState.RECREATED)
  ) {
    return (
      <Box>
        <BillingTooltipModal
          count={availableLimit?.availableLimit}
          type="SEND_DOC_TO_CHECK"
        >
          <LoadingButton
            key={1}
            color="primary"
            disabled={isDisabled || hasLegacyDuplicateAssignees}
            loading={isLoading}
            size="small"
            title="Отправить на проверку"
            onClick={() =>
              checkAndCommitOperation({
                onExceeded: openPaywall,
                operation: {
                  operationId: 'SEND_DOC_TO_CHECK',
                  operationCount: 1,
                  operationCallback: () => handleToInspection(act.id),
                },
              })
            }
          >
            На проверку
          </LoadingButton>
        </BillingTooltipModal>
      </Box>
    )
  }

  const handleToInspectionClick = () => {
    checkOperation({
      operationId: 'APPROVE_ACT',
      operationCallback: () => {
        handleToInspection(act.id)
      },
    })
  }

  return (
    <Box>
      <Typography gutterBottom sx={{ color: '0,0,0,0.6', fontSize: 12 }}>
        {inspectionState}
      </Typography>
      {act.inspectors?.map(
        ({ employmentId, inspectionState, approvingDeadlineDate }) => {
          if (
            inspectionState === PENDING &&
            employmentId === userEmploymentId
          ) {
            return (
              <Paper
                key={employmentId}
                elevation={6}
                sx={{ p: 0.5, my: 0.5, mx: -0.5 }}
              >
                <UserInfoPopoverWithStatus
                  key={act.id}
                  color="#333333"
                  deadline={approvingDeadlineDate}
                  icon={statusIcon(inspectionState)}
                  status={theStateWithDescription(inspectionState)}
                  statusColor={statusColor(inspectionState)}
                  userEmploymentId={employmentId}
                />
                <Stack direction="row" justifyContent="center" spacing={2}>
                  <Button
                    key={2}
                    color="accepted"
                    disabled={isDisabled}
                    size="small"
                    onClick={handleToInspectionClick}
                  >
                    Утвердить
                  </Button>
                  <DeviationDialog
                    additionalInfo={{ id: act.id, entityType: 'act' }}
                    documentType={act.actType}
                    initialInformation={act.name}
                    isDisabled={isDisabled}
                    subtitle={`Для документа ${act.name}`}
                    title="Записать отклонение"
                    onSubmit={handleDeviate}
                  />
                </Stack>
              </Paper>
            )
          }

          return (
            <Stack key={employmentId} direction="row">
              <UserInfoPopoverWithStatus
                key={act.id}
                color="#333333"
                deadline={
                  inspectionState === PENDING
                    ? approvingDeadlineDate
                    : undefined
                }
                icon={statusIcon(inspectionState)}
                status={theStateWithDescription(inspectionState)}
                statusColor={statusColor(inspectionState)}
                userEmploymentId={employmentId}
              />
            </Stack>
          )
        },
      )}
    </Box>
  )
}

export default InspectorAction
